import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { ReactComponent as ArrowLeft } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as Copy } from '../../../assets/icons/copy.svg';
import config from '../../../config/config';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import ocpiApi from '../../../utils/api/ocpi';
import CpoCdrsPanel from '../operations/cpo/CpoCdrsPanel';
import CpoCommandsPanel from '../operations/cpo/CpoCommandsPanel';
import CpoConnectionLogsPanel from '../operations/cpo/CpoConnectionLogsPanel';
import CpoLocationsPanel from '../operations/cpo/CpoLocationsPanel';
import CpoSessionsPanel from '../operations/cpo/CpoSessionsPanel';
import CpoTariffsPanel from '../operations/cpo/CpoTariffsPanel';
import CpoTokensPanel from '../operations/cpo/CpoTokensPanel';
// import CpoTokensPanel from './operations/cpo/CpoTokensPanel';
import EmspCdrsPanel from '../operations/emsp/EmspCdrsPanel';
import EmspCommandsPanel from '../operations/emsp/EmspCommandsPanel';
import EmspConnectionLogsPanel from '../operations/emsp/EmspConnectionLogsPanel';
import EmspLocationsPanel from '../operations/emsp/EmspLocationsPanel';
import EmspSessionsPanel from '../operations/emsp/EmspSessionsPanel';
import EmspTariffsPanel from '../operations/emsp/EmspTariffsPanel';
import EmspTokensPanel from '../operations/emsp/EmspTokensPanel';
// import EmspTokensPanel from './operations/emsp/EmspTokensPanel';
import VersionsPanel from '../operations/VersionsPanel';

interface ModuleAction {
  label: string;
  action: string;
  show: (isCpo: boolean) => boolean;
}

interface ModuleConfig {
  id: string;
  label: string;
  subtext: string;
  description: string;
  commRole: (isCpo: boolean) => 'SENDER' | 'RECEIVER';
  owner: (isCpo: boolean) => 'OWN' | 'PARTNER';
  receiverUrl: (connection: any, isCpo: boolean) => string;
  actions: ModuleAction[];
  renderExplorer: (properties: {
    connection: any;
    ourCountryCode: string;
    ourPartyId: string;
    partnerCountryCode: string;
    partnerPartyId: string;
    isCpo: boolean;
    // refreshTrigger: number;
  }) => React.ReactNode;
}

/**
 * Displays and manages OCPI connection details and actions.
 * @returns {any} Connection management page component.
 */
export default function ConnectionManagement(): JSX.Element {
  const { t } = useTranslation('ocpi');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * Copies the given text to the clipboard and dispatches a success notification toast.
   * @param {string} text - The text to copy to the clipboard.
   * @param {string} [label] - Optional label describing the copied item.
   */
  const copyToClipboard = (text: string, label?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    const message = label
      ? t('copiedToClipboard', { label })
      : t('copiedToClipboardGeneric');
    dispatch(setNotificationMessage({ message, status: true, type: 'success' }));
  };
  const { activePortal } = usePortal();
  const { activeProduct: reduxActiveProduct } = useAppSelector((state) => state.product);
  const { selectedRole } = useAppSelector((state) => state.ocpiConnections);

  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<any>(null);
  const [tenants, setTenants] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('locations');
  // const [refreshTrigger, setRefreshTrigger] = useState(0);

  const activeProduct =
    selectedRole?.role ||
    reduxActiveProduct ||
    (activePortal === PortalType.MOBILITY ? 'EMSP' : 'CPO');

  /**
   * Checks whether a role/partyId/countryCode triple matches one of our own registered
   * tenants. Used to unambiguously resolve which side of a connection is "ours" instead of
   * relying on the global active-product/selected-role guess, which breaks on a fresh page
   * load (deep link, browser refresh) or for loopback connections between our own tenants.
   * @param {string} [role] - The role to match.
   * @param {string} [partyId] - The party ID to match.
   * @param {string} [countryCode] - The country code to match.
   * @returns {boolean} True if a registered tenant matches all three fields.
   */
  const matchesOwnTenant = (role?: string, partyId?: string, countryCode?: string) =>
    tenants.some(
      (tenant: any) =>
        tenant.role?.toUpperCase() === role?.toUpperCase() &&
        tenant.partyId?.toUpperCase() === partyId?.toUpperCase() &&
        tenant.countryCode?.toUpperCase() === countryCode?.toUpperCase(),
    );

  const isWeSender = (() => {
    if (!connection) return false;
    const senderIsOurs = matchesOwnTenant(
      connection.senderRole,
      connection.senderPartyId,
      connection.senderCountryCode,
    );
    const receiverIsOurs = matchesOwnTenant(
      connection.role,
      connection.partyId,
      connection.countryCode,
    );
    // Unambiguous: only one side belongs to a tenant we own.
    if (senderIsOurs !== receiverIsOurs) return senderIsOurs;
    // Ambiguous (loopback connection between our own tenants, or tenants not loaded yet):
    // fall back to the currently selected/active role as a tiebreaker.
    return connection.senderRole?.toUpperCase() === activeProduct?.toUpperCase();
  })();

  let ourRole = '';
  let ourPartyId = '';
  let ourCountryCode = '';
  let partnerRole = '';
  let partnerPartyId = '';
  let partnerCountryCode = '';

  if (connection) {
    ourRole = isWeSender ? connection.senderRole : connection.role;
    ourPartyId = isWeSender ? connection.senderPartyId : connection.partyId;
    ourCountryCode = isWeSender ? connection.senderCountryCode : connection.countryCode;

    partnerRole = isWeSender ? connection.role : connection.senderRole;
    partnerPartyId = isWeSender ? connection.partyId : connection.senderPartyId;
    partnerCountryCode = isWeSender ? connection.countryCode : connection.senderCountryCode;
  }

  const isCpo = ourRole?.toUpperCase() === 'CPO';

  const MODULE_CONFIGS: ModuleConfig[] = [
    {
      id: 'versions',
      label: t('versions'),
      subtext: t('versions'),
      description: t('versions'),
      /**
       * Versions module always communicates as receiver.
       * @returns {string} 'RECEIVER'.
       */
      commRole: () => 'RECEIVER',
      /**
       * Versions module data is always owned by us.
       * @returns {string} 'OWN'.
       */
      owner: () => 'OWN',
      /**
       * Builds the versions endpoint URL from the OCPI API base URL.
       * @returns {string} The versions endpoint URL.
       */
      receiverUrl: () => `${config.ocpiApiUrl.replace(/\/$/, '')}/versions`,
      actions: [],
      /**
       * Renders the versions data explorer for the active connection.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The versions explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
      }) => (
        <VersionsPanel isCpo={cpoRole} partnerCountryCode={partnerCc} partnerPartyId={partnerPid} />
      ),
    },
    // {
    //   id: 'credentials',
    //   label: 'Credentials',
    //   subtext: 'CredentialsDto & Registration',
    //   description: 'CredentialsDto & Registration',
    //   commRole: () => 'RECEIVER',
    //   owner: () => 'OWN',
    //   receiverUrl: () => `${config.ocpiApiUrl.replace(/\/$/, '')}/credentials`,
    //   actions: [],
    //   renderExplorer: () => (
    //     <div className="connection-management__explorer-placeholder connection-management__explorer-placeholder--italic">
    //       Credentials exchange details. Use the top cards to inspect handshake status.
    //     </div>
    //   ),
    // },
    {
      id: 'locations',
      label: t('locations'),
      subtext: t('locations'),
      description: t('locations'),
      /**
       * Resolves the communication role for the locations module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'SENDER' for CPO, 'RECEIVER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'SENDER' : 'RECEIVER'),
      /**
       * Resolves the data owner for the locations module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'OWN' for CPO, 'PARTNER' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'OWN' : 'PARTNER'),
      /**
       * Resolves the locations endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The locations endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'locations',
        )?.url || '-',
      actions: [
        {
          label: t('syncCpoLocations'),
          action: 'SYNC',
          /**
           * Shows the sync action only for the CPO side.
           * @param {boolean} cpoRole - Whether the active side is a CPO.
           * @returns {boolean} True when the active side is a CPO.
           */
          show: (cpoRole: boolean) => cpoRole,
        },
        {
          label: t('broadcastUpdates'),
          action: 'BROADCAST',
          /**
           * Shows the broadcast action only for the CPO side.
           * @param {boolean} cpoRole - Whether the active side is a CPO.
           * @returns {boolean} True when the active side is a CPO.
           */
          show: (cpoRole: boolean) => cpoRole,
        },
        {
          label: t('pullLatestLocations'),
          action: 'PULL',
          /**
           * Shows the pull action only for the eMSP side.
           * @param {boolean} cpoRole - Whether the active side is a CPO.
           * @returns {boolean} True when the active side is not a CPO.
           */
          show: (cpoRole: boolean) => !cpoRole,
        },
      ],
      /**
       * Renders the locations data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The locations explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        ourCountryCode: ourCc,
        ourPartyId: ourPid,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
        // refreshTrigger: trigger,
      }) =>
        cpoRole ? (
          <CpoLocationsPanel countryCode={ourCc} partyId={ourPid} />
        ) : (
          <EmspLocationsPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ),
    },
    {
      id: 'sessions',
      label: t('sessions'),
      subtext: t('sessions'),
      description: t('sessions'),
      /**
       * Resolves the communication role for the sessions module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'SENDER' for CPO, 'RECEIVER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'SENDER' : 'RECEIVER'),
      /**
       * Resolves the data owner for the sessions module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'OWN' for CPO, 'PARTNER' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'OWN' : 'PARTNER'),
      /**
       * Resolves the sessions endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The sessions endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'sessions',
        )?.url || '-',
      actions: [],
      /**
       * Renders the sessions data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The sessions explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        ourCountryCode: ourCc,
        ourPartyId: ourPid,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
        // refreshTrigger: trigger,
      }) =>
        cpoRole ? (
          <CpoSessionsPanel countryCode={ourCc} partyId={ourPid} partnerCountryCode={partnerCc} partnerPartyId={partnerPid} />
        ) : (
          <EmspSessionsPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ),
    },
    // {
    //   id: 'tokens',
    //   label: 'Tokens',
    //   subtext: 'Tokens',
    //   description: 'Tokens',
    //   commRole: (cpoRole: boolean) => (cpoRole ? 'RECEIVER' : 'SENDER'),
    //   owner: (cpoRole: boolean) => (cpoRole ? 'PARTNER' : 'OWN'),
    //   receiverUrl: (conn: any) =>
    //     conn?.businessDetails?.endpoints?.find(
    //       (currentEndpoint: any) => currentEndpoint.identifier === 'tokens',
    //     )?.url || '-',
    //   actions: [],
    //   renderExplorer: ({
    //     isCpo: cpoRole,
    //     ourCountryCode: ourCc,
    //     ourPartyId: ourPid,
    //     partnerCountryCode: partnerCc,
    //     partnerPartyId: partnerPid,
    //     refreshTrigger: trigger,
    //   }) =>
    //     cpoRole ? (
    //       <CpoTokensPanel
    //         countryCode={ourCc}
    //         partyId={ourPid}
    //         filterCountryCode={partnerCc}
    //         filterPartyId={partnerPid}
    //         refreshTrigger={trigger}
    //       />
    //     ) : (
    //       <EmspTokensPanel
    //         countryCode={ourCc}
    //         partyId={ourPid}
    //         filterCountryCode={partnerCc}
    //         filterPartyId={partnerPid}
    //         refreshTrigger={trigger}
    //       />
    //     ),
    // },
    {
      id: 'tariffs',
      label: t('tariffs'),
      subtext: t('tariffs'),
      description: t('tariffs'),
      /**
       * Resolves the communication role for the tariffs module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'SENDER' for CPO, 'RECEIVER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'SENDER' : 'RECEIVER'),
      /**
       * Resolves the data owner for the tariffs module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'OWN' for CPO, 'PARTNER' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'OWN' : 'PARTNER'),
      /**
       * Resolves the tariffs endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The tariffs endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'tariffs',
        )?.url || '-',
      actions: [],
      /**
       * Renders the tariffs data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The tariffs explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        ourCountryCode: ourCc,
        ourPartyId: ourPid,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
        // refreshTrigger: trigger,
      }) =>
        cpoRole ? (
          <CpoTariffsPanel countryCode={ourCc} partyId={ourPid} />
        ) : (
          <EmspTariffsPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ),
    },
    {
      id: 'cdrs',
      label: t('cdrs'),
      subtext: t('chargingProfiles'),
      description: t('chargingProfiles'),
      /**
       * Resolves the communication role for the cdrs module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'SENDER' for CPO, 'RECEIVER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'SENDER' : 'RECEIVER'),
      /**
       * Resolves the data owner for the cdrs module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'OWN' for CPO, 'PARTNER' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'OWN' : 'PARTNER'),
      /**
       * Resolves the cdrs endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The cdrs endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'cdrs',
        )?.url || '-',
      actions: [],
      /**
       * Renders the cdrs data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The cdrs explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        ourCountryCode: ourCc,
        ourPartyId: ourPid,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
        // refreshTrigger: trigger,
      }) =>
        cpoRole ? (
          <CpoCdrsPanel countryCode={ourCc} partyId={ourPid} partnerCountryCode={partnerCc} partnerPartyId={partnerPid} />
        ) : (
          <EmspCdrsPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ),
    },
    {
      id: 'tokens',
      label: t('tokens'),
      subtext: t('tokens'),
      description: t('tokens'),
      /**
       * Resolves the communication role for the tokens module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'RECEIVER' for CPO, 'SENDER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'RECEIVER' : 'SENDER'),
      /**
       * Resolves the data owner for the tokens module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'PARTNER' for CPO, 'OWN' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'PARTNER' : 'OWN'),
      /**
       * Resolves the tokens endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The tokens endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'tokens',
        )?.url || '-',
      actions: [],
      /**
       * Renders the tokens data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The tokens explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        ourCountryCode: ourCc,
        ourPartyId: ourPid,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
        // refreshTrigger: trigger,
      }) =>
        cpoRole ? (
          <CpoTokensPanel countryCode={ourCc} partyId={ourPid} filterCountryCode={partnerCc} filterPartyId={partnerPid} />
        ) : (
          <EmspTokensPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ),
    },
    {
      id: 'commands',
      label: t('commands'),
      subtext: t('commands'),
      description: t('commands'),
      /**
       * Resolves the communication role for the commands module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'RECEIVER' for CPO, 'SENDER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'RECEIVER' : 'SENDER'),
      /**
       * Resolves the data owner for the commands module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'PARTNER' for CPO, 'OWN' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'PARTNER' : 'OWN'),
      /**
       * Resolves the commands endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The commands endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'commands',
        )?.url || '-',
      actions: [],
      /**
       * Renders the commands data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The commands explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
        ourCountryCode: ourCc,
        ourPartyId: ourPid,
        partnerCountryCode: partnerCc,
        partnerPartyId: partnerPid,
        // refreshTrigger: trigger,
      }) =>
        cpoRole ? (
          <CpoCommandsPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ) : (
          <EmspCommandsPanel
            countryCode={ourCc}
            partyId={ourPid}
            filterCountryCode={partnerCc}
            filterPartyId={partnerPid}
            // refreshTrigger={trigger}
          />
        ),
    },
    {
      id: 'logs',
      label: t('logs'),
      subtext: t('logs'),
      description: t('logs'),
      /**
       * Resolves the communication role for the activity logs module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'SENDER' | 'RECEIVER'} 'RECEIVER' for CPO, 'SENDER' otherwise.
       */
      commRole: (cpoRole: boolean) => (cpoRole ? 'RECEIVER' : 'SENDER'),
      /**
       * Resolves the data owner for the activity logs module.
       * @param {boolean} cpoRole - Whether the active side is a CPO.
       * @returns {'OWN' | 'PARTNER'} 'PARTNER' for CPO, 'OWN' otherwise.
       */
      owner: (cpoRole: boolean) => (cpoRole ? 'PARTNER' : 'OWN'),
      /**
       * Resolves the activity logs endpoint URL from the connection's business details.
       * @param {any} conn - The OCPI connection object.
       * @returns {string} The activity logs endpoint URL, or '-' if not found.
       */
      receiverUrl: (conn: any) =>
        conn?.businessDetails?.endpoints?.find(
          (currentEndpoint: any) => currentEndpoint.identifier === 'activityLogs',
        )?.url || '-',
      actions: [],
      /**
       * Renders the activity logs data explorer for the active role.
       * @param {object} properties - Explorer render properties.
       * @param {boolean} properties.isCpo - Whether the active side is a CPO.
       * @param {string} properties.ourCountryCode - Our country code.
       * @param {string} properties.ourPartyId - Our party ID.
       * @param {string} properties.partnerCountryCode - Partner's country code.
       * @param {string} properties.partnerPartyId - Partner's party ID.
       * @returns {React.ReactNode} The activity logs explorer panel.
       */
      renderExplorer: ({
        isCpo: cpoRole,
      }) =>
        cpoRole ? (
          <CpoConnectionLogsPanel
            connectionId={id || ''}
          />
        ) : (
          <EmspConnectionLogsPanel
            connectionId={id || ''}
          />
        ),
    },
  ];

  const activeTabConfig = MODULE_CONFIGS.find((m) => m.id === activeTab) || MODULE_CONFIGS[0];

  const modules = MODULE_CONFIGS;

  /**
   * Retrieves our own registered OCPI tenants, used to unambiguously resolve which side of
   * the connection belongs to us.
   * @returns {Promise<void>} Resolves when tenant retrieval is complete.
   */
  const fetchTenants = async () => {
    try {
      const response = await ocpiApi.listTenants();
      const rawData = response.data || {};
      const nestedTenants = Array.isArray(rawData.tenants) ? rawData.tenants : [];
      setTenants(Array.isArray(rawData) ? rawData : nestedTenants);
    } catch (error) {
      // Non-fatal: falls back to the active-role heuristic when tenants can't be loaded.
    }
  };

  /**
   * Retrieves the selected OCPI connection and updates the component state.
   * @returns {Promise<void>} Resolves when connection retrieval is complete.
   */
  const fetchConnection = async () => {
    setLoading(true);
    try {
      const response = await ocpiApi.listConnections(activeProduct);
      const allConns = response.data || [];
      const currentConn = allConns.find((c: any) => c._id === id || c.id === id);
      if (currentConn) {
        setConnection(currentConn);
      } else {
        dispatch(
          setNotificationMessage({
            message: t('connectionNotFound'),
            status: true,
            type: 'error',
          }),
        );
        navigate('/ev-admin/ocpi');
      }
    } catch (error) {
      dispatch(
        setNotificationMessage({
          message: t('failedToFetchConnectionDetails'),
          status: true,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setPageTitle(t('ocpiRoaming')));
    fetchTenants();
    if (id) fetchConnection();
  }, [id, dispatch, t]);

  // /**
  //  * Initiates a manual action for the selected OCPI module.
  //  * @param {any} module - The OCPI module to perform the action on (e.g., 'locations', 'tariffs').
  //  * @param {any} action - The specific action to perform (e.g., 'PUSH', 'PULL', 'SYNC', 'BROADCAST').
  //  */
  // const handleManualAction = async (module: string, action: string) => {
  //   try {
  //     if (module === 'locations' && action === 'SYNC') {
  //       dispatch(setNotificationMessage({ message: 'Syncing CPO locations...', type: 'success' }));
  //       await ocpiApi.syncInternalCpoLocations(ourCountryCode, ourPartyId);
  //       dispatch(
  //         setNotificationMessage({ message: 'Locations synced successfully!', type: 'success' }),
  //       );
  //       fetchConnection();
  //     } else if (module === 'locations' && action === 'BROADCAST') {
  //       dispatch(setNotificationMessage({ message: 'Broadcasting updates...', type: 'success' }));
  //       await ocpiApi.broadcastInternalCpoLocations(ourCountryCode, ourPartyId);
  //       dispatch(setNotificationMessage({ message: 'Broadcast triggered!', type: 'success' }));
  //     } else if (module === 'locations' && action === 'PULL') {
  //       dispatch(
  //         setNotificationMessage({ message: 'Pulling partner locations...', type: 'success' }),
  //       );
  //       await ocpiApi.pullInternalEmspLocations(
  //         ourCountryCode,
  //         ourPartyId,
  //         connection._id || connection.id,
  //       );
  //       dispatch(
  //         setNotificationMessage({ message: 'Locations pulled successfully!', type: 'success' }),
  //       );
  //       setRefreshTrigger((previousTrigger) => previousTrigger + 1);
  //       fetchConnection();
  //     } else {
  //       dispatch(
  //         setNotificationMessage({
  //           message: `Triggering ${action} for ${module} (Simulated)...`,
  //           type: 'success',
  //         }),
  //       );
  //     }
  //   } catch (error: any) {
  //     dispatch(
  //       setNotificationMessage({
  //         message: error.response?.data?.message || `Failed to trigger ${action}`,
  //         type: 'error',
  //       }),
  //     );
  //   }
  // };

  /**
   * Determines if a given OCPI module is available for the active connection.
   * @param {string} moduleId - The ID of the module to check.
   * @returns {boolean} True if the module is available, false otherwise.
   */
  const isModuleAvailable = (moduleId: string) => {
    if (moduleId === 'versions' || moduleId === 'credentials') return true;
    return (
      connection?.businessDetails?.endpoints?.some(
        (endpoint: any) => endpoint.identifier === moduleId,
      ) ?? false
    );
  };

  /**
   * Resolves the metadata fields dynamically for a given module.
   * @param {string} moduleId - The ID of the module to retrieve metadata for.
   * @returns {object} An object containing the module metadata values.
   */
  const getModuleMetadata = (moduleId: string) => {
    const configItem = MODULE_CONFIGS.find((m) => m.id === moduleId) || MODULE_CONFIGS[0];
    const endpoint = connection?.businessDetails?.endpoints?.find(
      (currentEndpoint: any) => currentEndpoint.identifier === moduleId,
    );

    let present = 'No';
    if (endpoint || moduleId === 'versions' || moduleId === 'credentials') {
      present = 'Yes';
    }

    return {
      id: configItem.id,
      description: configItem.description,
      present,
      commRole: configItem.commRole(isCpo),
      owner: configItem.owner(isCpo),
      receiverUrl: configItem.receiverUrl(connection, isCpo),
      hasCredentialsToken: connection?.tokenB || connection?.tokenC ? 'Yes' : 'No',
    };
  };

  if (loading) {
    return (
      <div className="ocpi-page-container">
        <div className="loader-wrap">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  if (!connection) return <div />;

  const ourTokenValue =
    connection.ourTokenRole === 'RECEIVER' ? connection.tokenC : connection.tokenB;
  const ourTokenText = ourTokenValue ? `${ourTokenValue.slice(0, 20)}...` : '-';

  const partnerTokenValue =
    connection.ourTokenRole === 'RECEIVER' ? connection.tokenB : connection.tokenC;
  const partnerTokenText = partnerTokenValue ? `${partnerTokenValue.slice(0, 20)}...` : '-';

  return (
    <div className="ocpi-page-container">
      {/* Breadcrumbs */}
      <div className="connection-management__breadcrumbs">
        <span>{t('dashboard')}</span>
        <span>/</span>
        <span>{t('ocpiNetwork')}</span>
        <span>/</span>
        <span className="connection-management__breadcrumbs-active">
          {t('connection')} {connection._id || connection.id}
        </span>
      </div>

      {/* Page Header */}
      <div className="connection-management__header">
        <ArrowLeft
          className="connection-management__back-button"
          onClick={() => navigate('/ev-admin/ocpi')}
        />
        <div>
          <h1 className="connection-management__header-title">{t('ocpiConnectionWorkspace')}</h1>
          <p className="connection-management__header-subtitle">
            {t('ocpiConnectionWorkspaceSubtitle')}
          </p>
        </div>
      </div>

      {/* Navigation and Badges Row */}
      <div className="connection-management__nav-row">
        <div className="connection-management__badges">
          <span
            className={`connection-management__badge connection-management__badge--status-${
              connection.status === 'CONNECTED' ? 'connected' : 'pending'
            }`}>
            {connection.status === 'CONNECTED'
              ? t('registered')
              : connection.status || t('pending')}
          </span>
          <span className="connection-management__badge connection-management__badge--our-role">
            {t('ourRole')}: {ourRole?.toUpperCase() || t('na')}
          </span>
          <span className="connection-management__badge connection-management__badge--client-role">
            {t('clientRole')}: {partnerRole?.toUpperCase() || t('na')}
          </span>
          <span className="connection-management__badge connection-management__badge--version">
            OCPI 2.2.1
          </span>
        </div>
      </div>

      {/* TOP CARDS ROW */}
      <div className="connection-management__cards">
        {/* Card 1: Our Side */}
        <div className="connection-management__card">
          <div className="connection-management__card-header">
            <span className="connection-management__card-header-label">{t('ourSide')}</span>
            <span className="connection-management__card-header-value">
              {ourRole?.toUpperCase() || t('na')}
            </span>
          </div>
          <div className="connection-management__card-body">
            <div className="connection-management__card-row">
              <span className="connection-management__card-row-label">{t('tokenRole')}</span>
              <strong className="connection-management__card-row-value">
                {connection.ourTokenRole === 'RECEIVER' ? t('receiver') : t('sender')}
              </strong>
            </div>
            <div className="connection-management__card-row">
              <span className="connection-management__card-row-label">{t('tokenType')}</span>
              <strong className="connection-management__card-row-value">
                {connection.ourTokenRole === 'RECEIVER' ? 'TOKEN_C' : 'TOKEN_B'}
              </strong>
            </div>
            <div className="connection-management__card-row connection-management__card-row--center">
              <span className="connection-management__card-row-label">{t('tokenValue')}</span>
              <div className="connection-management__card-row-value-wrap">
                <code
                  role="presentation"
                  className="connection-management__code"
                  title={t('clickToCopy')}
                  onClick={() => copyToClipboard(ourTokenValue, t('token'))}>
                  {ourTokenText}
                </code>
                <MyButton
                  buttonType="none"
                  label=""
                  disableClass="connection-management__copy-btn"
                  hoverLabel={t('copyToken')}
                  onClickFunc={() => copyToClipboard(ourTokenValue, t('token'))}
                  svgIcon={<Copy height={14} width={14} />}
                />
              </div>
            </div>
            <div className="connection-management__card-row connection-management__card-row--center">
              <span className="connection-management__card-row-label">{t('versionUrl')}</span>
              <div className="connection-management__card-row-value-wrap">
                <code
                  role="presentation"
                  className="connection-management__code connection-management__code--truncate"
                  title={t('clickToCopy')}
                  onClick={() =>
                    copyToClipboard(
                      `${config.ocpiApiUrl.replace(/\/$/, '')}/versions`,
                      t('versionUrl'),
                    )
                  }>
                  {`${config.ocpiApiUrl.replace(/\/$/, '')}/versions`}
                </code>
                <MyButton
                  buttonType="none"
                  label=""
                  disableClass="connection-management__copy-btn"
                  hoverLabel={t('copyUrl')}
                  onClickFunc={() =>
                    copyToClipboard(
                      `${config.ocpiApiUrl.replace(/\/$/, '')}/versions`,
                      t('versionUrl'),
                    )
                  }
                  svgIcon={<Copy height={14} width={14} />}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Connection details */}
        <div className="connection-management__card connection-management__details-card">
          <span className="connection-management__details-label">
            {t('connection')} {connection._id || connection.id}
          </span>
          <div className="connection-management__details-flow">
            <div className="connection-management__details-dot" />
            <div className="connection-management__details-dash" />
            <span className="connection-management__details-status-badge">
              {connection.status === 'CONNECTED'
                ? t('registered')
                : connection.status || t('pending')}
            </span>
            <div className="connection-management__details-dash" />
            <div className="connection-management__details-dot" />
          </div>
          <div className="connection-management__details-meta">
            <div className="connection-management__details-meta-row">
              <span className="connection-management__details-meta-label">{t('platform')}:</span>
              <strong className="connection-management__details-meta-value">
                OCPI-{(connection.masterTenantId || 'PLATFORM').toUpperCase()}
              </strong>
            </div>
            <div className="connection-management__details-meta-row">
              <span className="connection-management__details-meta-label">{t('platformRole')}:</span>
              <strong className="connection-management__details-meta-value">
                {connection._id || connection.id}
              </strong>
            </div>
            <div className="connection-management__details-meta-row connection-management__details-meta-row--center">
              <span className="connection-management__details-meta-label">{t('baseUrl')}:</span>
              <div className="connection-management__card-row-value-wrap">
                <code
                  role="presentation"
                  className="connection-management__code connection-management__code--truncate-wide"
                  title={t('clickToCopy')}
                  onClick={() =>
                    copyToClipboard(config.ocpiApiUrl.replace(/\/ocpi\/?$/, ''), t('baseUrl'))
                  }>
                  {config.ocpiApiUrl.replace(/\/ocpi\/?$/, '')}
                </code>
                <MyButton
                  buttonType="none"
                  label=""
                  disableClass="connection-management__copy-btn"
                  hoverLabel={t('copyUrl')}
                  onClickFunc={() =>
                    copyToClipboard(config.ocpiApiUrl.replace(/\/ocpi\/?$/, ''), t('baseUrl'))
                  }
                  svgIcon={<Copy height={14} width={14} />}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Client Side */}
        <div className="connection-management__card">
          <div className="connection-management__card-header">
            <span className="connection-management__card-header-label">{t('clientSide')}</span>
            <span className="connection-management__card-header-value">
              {partnerRole?.toUpperCase() || t('na')}
            </span>
          </div>
          <div className="connection-management__card-body">
            <div className="connection-management__card-row">
              <span className="connection-management__card-row-label">{t('tokenRole')}</span>
              <strong className="connection-management__card-row-value">
                {connection.ourTokenRole === 'RECEIVER' ? t('sender') : t('receiver')}
              </strong>
            </div>
            <div className="connection-management__card-row">
              <span className="connection-management__card-row-label">{t('tokenType')}</span>
              <strong className="connection-management__card-row-value">
                {connection.ourTokenRole === 'RECEIVER' ? 'TOKEN_B' : 'TOKEN_C'}
              </strong>
            </div>
            <div className="connection-management__card-row connection-management__card-row--center">
              <span className="connection-management__card-row-label">{t('versionUrl')}</span>
              <div className="connection-management__card-row-value-wrap">
                <code
                  role="presentation"
                  className="connection-management__code connection-management__code--truncate-wider"
                  title={connection.versionsUrl ? t('clickToCopy') : undefined}
                  onClick={() =>
                    connection.versionsUrl &&
                    copyToClipboard(connection.versionsUrl, t('versionUrl'))
                  }>
                  {connection.versionsUrl || '-'}
                </code>
                {connection.versionsUrl && (
                  <MyButton
                    buttonType="none"
                    label=""
                    disableClass="connection-management__copy-btn"
                    hoverLabel={t('copyUrl')}
                    onClickFunc={() =>
                      copyToClipboard(connection.versionsUrl, t('versionUrl'))
                    }
                    svgIcon={<Copy height={14} width={14} />}
                  />
                )}
              </div>
            </div>
            <div className="connection-management__card-row connection-management__card-row--center">
              <span className="connection-management__card-row-label">{t('tokenValue')}</span>
              <div className="connection-management__card-row-value-wrap">
                <code
                  role="presentation"
                  className="connection-management__code"
                  title={t('clickToCopy')}
                  onClick={() => copyToClipboard(partnerTokenValue, t('token'))}>
                  {partnerTokenText}
                </code>
                <MyButton
                  buttonType="none"
                  label=""
                  disableClass="connection-management__copy-btn"
                  hoverLabel={t('copyToken')}
                  onClickFunc={() => copyToClipboard(partnerTokenValue, t('token'))}
                  svgIcon={<Copy height={14} width={14} />}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODULE SUMMARY TABS SECTION */}
      <div className="connection-management__modules">
        <h3 className="connection-management__modules-title">{t('moduleSummary')}</h3>
        <p className="connection-management__modules-subtitle">
          {t('moduleSummarySubtitle')}
        </p>

        <div className="connection-management__modules-tabs">
          {modules.map((moduleData) => {
            const active = activeTab === moduleData.id;
            const available = isModuleAvailable(moduleData.id);
            return (
              <button
                key={moduleData.id}
                type="button"
                onClick={() => setActiveTab(moduleData.id)}
                className={`connection-management__module-tab ${
                  active ? 'connection-management__module-tab--active' : ''
                }`}>
                <div className="connection-management__module-tab-row">
                  <span className="connection-management__module-tab-label">
                    {moduleData.label}
                  </span>
                  <span
                    className={`connection-management__module-tab-badge ${
                      available
                        ? 'connection-management__module-tab-badge--available'
                        : 'connection-management__module-tab-badge--unavailable'
                    }`}>
                    {available ? t('available') : t('na')}
                  </span>
                </div>
                <span className="connection-management__module-tab-subtext">
                  {moduleData.subtext}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* DATA EXPLORER & METADATA SECTION */}
      <div className="connection-management__explorer">
        <div className="connection-management__explorer-header">
          <div>
            <h3 className="connection-management__explorer-title">
              {t('moduleDataExplorer', { module: activeTabConfig.label })}
            </h3>
          </div>
          {/* <div className="connection-management__explorer-actions">
            {activeTabConfig.actions
              .filter((act) => act.show(isCpo))
              .map((act) => (
                <MyButton
                  key={act.action}
                  label={act.label}
                  // onClickFunc={() => handleManualAction(activeTab, act.action)}
                  buttonType="outline"
                />
              ))}
          </div> */}
        </div>

        {/* Upper section: Explorer Content */}
        <div className="connection-management__explorer-content">
          {activeTabConfig.renderExplorer({
            connection,
            ourCountryCode,
            ourPartyId,
            partnerCountryCode,
            partnerPartyId,
            isCpo,
            // refreshTrigger,
          })}
        </div>

        {/* Lower section: Module Metadata Card */}
        <div className="connection-management__metadata">
          <h4 className="connection-management__metadata-title">{t('moduleMetadata')}</h4>
          {(() => {
            const meta = getModuleMetadata(activeTab);
            const metadataRows = [
              { label: t('moduleId'), value: t(meta.id) },
              { label: t('description'), value: meta.description },
              { label: t('present'), value: meta.present === 'Yes' ? t('yes') : t('no') },
              {
                label: t('communicationRole'),
                value: meta.commRole === 'RECEIVER' ? t('receiver') : t('sender'),
              },
              {
                label: t('moduleDataOwner'),
                value: meta.owner === 'OWN' ? t('own') : t('partner'),
              },
              { label: t('receiverUrl'), value: meta.receiverUrl, isCode: true },
              {
                label: t('hasCredentialsToken'),
                value: meta.hasCredentialsToken === 'Yes' ? t('yes') : t('no'),
              },
            ];

            return (
              <div className="connection-management__metadata-table">
                {metadataRows.map((row) => (
                  <div key={row.label} className="connection-management__metadata-row">
                    <span className="connection-management__metadata-label">{row.label}</span>
                    {row.isCode ? (
                      <code className="connection-management__metadata-code">{row.value}</code>
                    ) : (
                      <span className="connection-management__metadata-value">{row.value}</span>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
